<template>
  <div class="shorts-page" :class="{'show-controls': showControls}" @click="handleContainerTap">
    <Preloader :backgroundColor="preloaderColor"></Preloader>

    <!-- Botón X fijo para cerrar (siempre visible) -->
    <button @click="closeShorts" class="close-btn" title="Cerrar Shorts">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>

    <!-- Sonido: el autoplay con audio requiere un gesto del usuario -->
    <button @click.stop="toggleSound" class="sound-btn" :title="soundOn ? 'Silenciar' : 'Activar sonido'">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M11 5L6 9H2v6h4l5 4V5z"/>
        <path v-if="soundOn" d="M15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14"/>
        <path v-else d="M23 9l-6 6M17 9l6 6"/>
      </svg>
      <span v-if="!soundOn">Activar sonido</span>
    </button>

    <!-- Header minimalista -->
    <div class="shorts-header">
      <router-link to="/news" class="back-btn" title="Volver">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </router-link>
      <h1 class="shorts-title">Shorts</h1>
    </div>

    <!-- Tab/Handle para abrir drawer (solo móvil) -->
    <button @click.stop="toggleDrawer" class="drawer-handle" :class="{'drawer-handle--open': showDrawer}">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline :points="showDrawer ? '15 18 9 12 15 6' : '9 18 15 12 9 6'"/>
      </svg>
    </button>

    <!-- Drawer lateral (solo móvil) -->
    <div class="drawer-overlay" :class="{'drawer-overlay--visible': showDrawer}" @click="closeDrawer"></div>
    <div class="drawer-menu" :class="{'drawer-menu--open': showDrawer}">
      <div class="drawer-content">
        <!-- Botones de acción dentro del drawer -->
        <button
          @click.stop="toggleFavorite(videos[currentVideoIndex])"
          class="drawer-action-btn"
          :class="{'drawer-action-btn--active': isFavorite(videos[currentVideoIndex])}"
          v-if="videos[currentVideoIndex]"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
          <span class="drawer-action-label">{{ isFavorite(videos[currentVideoIndex]) ? 'Guardado' : 'Guardar' }}</span>
        </button>

        <button @click.stop="shareVideo(videos[currentVideoIndex])" class="drawer-action-btn" v-if="videos[currentVideoIndex]">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="18" cy="5" r="3"/>
            <circle cx="6" cy="12" r="3"/>
            <circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          <span class="drawer-action-label">Compartir</span>
        </button>

        <button @click.stop="openInPlatform(videos[currentVideoIndex])" class="drawer-action-btn" v-if="videos[currentVideoIndex]">
          <!-- Ícono de YouTube -->
          <svg v-if="getVideoType(videos[currentVideoIndex]) === 'youtube'" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
          </svg>
          <!-- Ícono de TikTok -->
          <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
          </svg>
          <span class="drawer-action-label">{{ getVideoType(videos[currentVideoIndex]) === 'tiktok' ? 'TikTok' : 'YouTube' }}</span>
        </button>
      </div>
    </div>

    <!-- Contenedor de shorts con scroll snap -->
    <div class="shorts-container" ref="container" @scroll="handleScroll">
      <div
        v-for="(video, index) in videos"
        :key="video.id || video.link"
        class="short-item"
        :data-index="index"
      >
        <!-- Video iframe o embed -->
        <div class="video-wrapper">
          <!-- Renderizar solo el video actual y adyacentes (±1) -->
          <template v-if="shouldRenderVideo(index)">
            <!-- TikTok usando iframe directo con Player API -->
            <iframe
              v-if="getVideoType(video) === 'tiktok'"
              :key="`tiktok-${index}-${videoRenderKey}`"
              :src="getEmbedUrl(video)"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
              allowfullscreen
              playsinline
              class="video-iframe tiktok-iframe"
              scrolling="no"
            ></iframe>

            <!-- YouTube usando iframe -->
            <iframe
              v-if="getVideoType(video) === 'youtube'"
              :key="`youtube-${index}-${videoRenderKey}`"
              :src="getEmbedUrl(video)"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
              allowfullscreen
              playsinline
              class="video-iframe youtube-iframe"
              @load="listenYouTube"
              scrolling="no"
            ></iframe>
          </template>

          <!-- Placeholder cuando no está renderizado -->
          <div
            v-else
            class="video-placeholder"
          >
            <img v-if="video.image && !video.image.includes('data:image')" :src="video.image" :alt="video.title" />
            <div class="play-overlay">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Acciones laterales -->
        <div class="video-info">
          <div class="video-actions">
            <!-- Favorito -->
            <button
              @click.stop="toggleFavorite(video)"
              class="action-btn"
              :class="{'action-btn--active': isFavorite(video)}"
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <span class="action-label">{{ isFavorite(video) ? 'Guardado' : 'Guardar' }}</span>
            </button>

            <!-- Compartir -->
            <button @click.stop="shareVideo(video)" class="action-btn">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              <span class="action-label">Compartir</span>
            </button>

            <!-- Ver en plataforma original -->
            <button @click.stop="openInPlatform(video)" class="action-btn">
              <!-- Ícono de YouTube -->
              <svg v-if="getVideoType(video) === 'youtube'" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
              </svg>
              <!-- Ícono de TikTok -->
              <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
              <span class="action-label">{{ getVideoType(video) === 'tiktok' ? 'TikTok' : 'YouTube' }}</span>
            </button>
          </div>
        </div>

        <!-- Indicadores de navegación -->
        <div class="navigation-hints">
          <div v-if="index > 0" class="nav-hint nav-hint--up">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
            </svg>
          </div>
          <div v-if="index < videos.length - 1" class="nav-hint nav-hint--down">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast de compartido -->
    <transition name="toast-fade">
      <div v-if="showShareToast" class="share-toast">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        Enlace copiado
      </div>
    </transition>
  </div>
</template>

<script>
import Preloader from '../../../components/Preloader';
import bookmarksService from '../../../services/bookmarks.js';
import { timeAgo } from '../../../utils/time.js';
import { copyShareUrl } from '../../../utils/shareUtils.js';
import feedChunksConfig from '../../../config/feedChunks.config';

export default {
  name: 'Shorts',
  components: {
    Preloader
  },
  data() {
    return {
      videos: [],
      currentVideoIndex: 0,
      updateTrigger: 0,
      showShareToast: false,
      preloaderColor: 'rgba(0, 0, 0, 0.95)',
      isScrolling: false,
      scrollTimeout: null,
      metadata: null,
      videoRenderKey: 0,
      // Si el usuario ya interactuó con la página (p.ej. llegó con un click), el navegador permite audio
      soundOn: !!(navigator.userActivation && navigator.userActivation.hasBeenActive),
      showControls: false,       // Control para mostrar/ocultar header en móvil
      controlsTimeout: null,     // Timeout para auto-hide
      showDrawer: false,         // Control para drawer lateral (móvil)
      // Propiedades para lazy loading de chunks
      currentChunkIndex: -1,     // Índice del último chunk cargado (-1 = ninguno)
      totalChunksAvailable: 0,   // Total de chunks disponibles
      isLoadingChunk: false,     // Flag para evitar cargas duplicadas
      loadMoreThreshold: 5,      // Cargar cuando queden 5 videos por ver
      allChunksLoaded: false     // Flag cuando ya no hay más chunks
    };
  },
  watch: {
    currentVideoIndex(newIndex, oldIndex) {
      console.log(`📹 Video changed: ${oldIndex} → ${newIndex}`);

      // Incrementar key para forzar re-renderizado de videos adyacentes
      this.videoRenderKey++;
    }
  },
  methods: {
    timeAgo,
    // Función para aleatorizar array (Fisher-Yates shuffle)
    shuffleArray(array) {
      const shuffled = [...array]; // Crear copia para no mutar original
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },
    shuffleByAuthor(posts) {
      const groups = posts.reduce((acc, post) => {
        acc[post.author] ??= [];
        acc[post.author].push(post);
        return acc;
      }, {});

      // Barajamos los autores
      const authors = Object.keys(groups);
      for (let i = authors.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [authors[i], authors[j]] = [authors[j], authors[i]];
      }

      // Mezclamos tomando un post de cada autor por turnos
      const result = [];
      let done = false;
      while (!done) {
        done = true;
        for (const a of authors) {
          if (groups[a].length > 0) {
            result.push(groups[a].shift());
            done = false;
          }
        }
      }
      return result;
    },
    shouldRenderVideo(index) {
      // Renderizar SOLO el video actual para evitar que múltiples videos
      // se reproduzcan simultáneamente
      return index === this.currentVideoIndex;
    },
    async loadChunk(chunkIndex) {
      // Evitar cargas duplicadas
      if (this.isLoadingChunk) {
        console.log('⏳ Ya hay una carga en progreso, esperando...');
        return;
      }

      // Verificar si ya cargamos todos los chunks
      if (chunkIndex >= this.totalChunksAvailable) {
        console.log('✅ Todos los chunks ya fueron cargados');
        this.allChunksLoaded = true;
        return;
      }

      this.isLoadingChunk = true;

      try {
        const chunkFile = `shorts-all-chunk-${chunkIndex}.json`;
        const chunkUrl = `${feedChunksConfig.baseUrl}${chunkFile}`;

        console.log(`⬇️ Cargando chunk ${chunkIndex}...`);

        const response = await fetch(chunkUrl);
        if (response.status === 404) {
          console.log(`✅ No hay más chunks (chunk ${chunkIndex} no existe)`);
          this.allChunksLoaded = true;
          return;
        }
        if (!response.ok) {
          throw new Error(`Error al cargar chunk ${chunkIndex}`);
        }

        const items = await response.json();

        // Transformar datos
        const transformedVideos = items.map(item => ({
          id: item.id || item.link || `fallback-${item.title}-${item.pubDate}`,
          title: item.title || '',
          link: item.link || '',
          pubDate: item.pubDate ? new Date(item.pubDate) : null,
          description: item.description || '',
          category: item.sourceCategory || 'Sin categoría',
          sourceCategory: item.sourceCategory || 'Sin categoría',
          sourceType: item.sourceType || 'youtube',
          sourceTitle: item.sourceTitle || '',
          author: item.author || 'Desconocido',
          image: item.image || null,
          isShortVideo: item.isShortVideo
        }));

        // Shuffle del chunk individual
        const shuffledChunk = this.shuffleByAuthor(transformedVideos);

        // Agregar al array de videos
        this.videos.push(...shuffledChunk);

        // Actualizar índice del chunk
        this.currentChunkIndex = chunkIndex;

        console.log(`✅ Chunk ${chunkIndex} cargado: ${items.length} videos`);
        console.log(`📺 Total videos en memoria: ${this.videos.length}`);

      } catch (error) {
        console.error(`❌ Error cargando chunk ${chunkIndex}:`, error);
      } finally {
        this.isLoadingChunk = false;
      }
    },
    checkAndLoadNextChunk() {
      // Si ya estamos cargando o ya cargamos todo, no hacer nada
      if (this.isLoadingChunk || this.allChunksLoaded) {
        return;
      }

      // Calcular cuántos videos quedan por ver
      const remainingVideos = this.videos.length - this.currentVideoIndex - 1;

      console.log(`🔍 Videos restantes: ${remainingVideos}`);

      // Si quedan menos videos que el threshold, cargar siguiente chunk
      if (remainingVideos <= this.loadMoreThreshold) {
        const nextChunkIndex = this.currentChunkIndex + 1;

        console.log(`⚡ Threshold alcanzado (${remainingVideos} <= ${this.loadMoreThreshold}), cargando chunk ${nextChunkIndex}...`);

        this.loadChunk(nextChunkIndex);
      }
    },
    async loadVideos() {
      try {
        // Cargar metadata primero
        const metadataUrl = `${feedChunksConfig.baseUrl}metadata.json`;
        const metadataResponse = await fetch(metadataUrl);
        if (!metadataResponse.ok) throw new Error('No se pudo cargar metadata');
        this.metadata = await metadataResponse.json();

        // Obtener información de Shorts
        // Si metadata no trae "shorts", cargar chunks hasta que uno devuelva 404
        this.totalChunksAvailable = this.metadata.shorts?.totalChunks || Infinity;

        console.log(`📊 Total chunks disponibles: ${this.totalChunksAvailable}`);
        console.log(`🚀 Iniciando carga lazy: solo primer chunk`);

        // Cargar SOLO el primer chunk
        await this.loadChunk(0);

        console.log(`🎬 Primer chunk cargado: ${this.videos.length} videos`);
        console.log(`📺 YouTube: ${this.videos.filter(v => v.sourceType === 'youtube').length}`);
        console.log(`🎵 TikTok: ${this.videos.filter(v => v.sourceType === 'tiktok').length}`);

        // Debug: mostrar primer video
        if (this.videos.length > 0) {
          console.log('Primer video:', this.videos[0]);
        }
      } catch (error) {
        console.error('Error loading videos:', error);
      }
    },
    getVideoType(video) {
      return video.sourceType || 'youtube';
    },
    extractYouTubeId(url) {
      if (!url) return null;

      if (url.includes('youtube.com/watch?v=')) {
        return url.split('v=')[1].split('&')[0];
      }
      if (url.includes('youtu.be/')) {
        return url.split('youtu.be/')[1].split('?')[0];
      }
      if (url.includes('youtube.com/shorts/')) {
        return url.split('shorts/')[1].split('?')[0];
      }
      return null;
    },
    extractTikTokId(url) {
      if (!url) return null;

      // Formato: https://www.tiktok.com/@usuario/video/ID
      if (url.includes('tiktok.com') && url.includes('/video/')) {
        const parts = url.split('/video/');
        if (parts[1]) {
          return parts[1].split('?')[0];
        }
      }
      return null;
    },
    getEmbedUrl(video) {
      const videoType = this.getVideoType(video);

      if (videoType === 'tiktok') {
        const videoId = this.extractTikTokId(video.link);
        if (!videoId) {
          console.error('No se pudo extraer ID de TikTok:', video.link);
          return '';
        }
        // Autoplay: el navegador lo fuerza en silencio sin gesto del usuario;
        // el audio se activa por postMessage en handlePlayerMessage
        // music_info=1 muestra información de la música
        // description=1 muestra la descripción
        const embedUrl = `https://www.tiktok.com/player/v1/${videoId}?autoplay=1&controls=1&music_info=1&description=1`;
        console.log('TikTok Player URL:', embedUrl);
        return embedUrl;
      } else {
        // YouTube por defecto
        const videoId = this.extractYouTubeId(video.link);
        if (!videoId) {
          console.error('No se pudo extraer ID de YouTube:', video.link);
          return '';
        }
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&enablejsapi=1&rel=0&modestbranding=1&fs=1&playsinline=1`;
      }
    },
    sendPlayerCommand(win, type, cmd) {
      if (!win) return;
      if (type === 'tiktok') {
        win.postMessage({ 'x-tiktok-player': true, type: cmd, value: null }, 'https://www.tiktok.com');
      } else {
        const func = { play: 'playVideo', mute: 'mute', unMute: 'unMute' }[cmd];
        win.postMessage(JSON.stringify({ event: 'command', func, args: [] }), 'https://www.youtube.com');
      }
    },
    listenYouTube(event) {
      // Handshake para que el iframe de YouTube nos envíe eventos (onReady)
      event.target.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), 'https://www.youtube.com');
    },
    handlePlayerMessage(event) {
      if (event.origin !== 'https://www.youtube.com' && event.origin !== 'https://www.tiktok.com') return;
      let data = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (e) { return; }
      }
      if (!data || (data.event !== 'onReady' && data.type !== 'onPlayerReady')) return;
      // Arranca en silencio (siempre permitido) y activa audio si el usuario lo pidió
      const type = event.origin === 'https://www.tiktok.com' ? 'tiktok' : 'youtube';
      this.sendPlayerCommand(event.source, type, 'play');
      if (this.soundOn) this.sendPlayerCommand(event.source, type, 'unMute');
    },
    toggleSound() {
      this.soundOn = !this.soundOn;
      const iframe = this.$el.querySelector('.video-iframe');
      if (iframe) {
        this.sendPlayerCommand(iframe.contentWindow, this.getVideoType(this.videos[this.currentVideoIndex]), this.soundOn ? 'unMute' : 'mute');
      }
    },
    handleScroll() {
      // Detectar el video más cercano al centro de la pantalla
      if (this.isScrolling) {
        clearTimeout(this.scrollTimeout);
      }

      this.isScrolling = true;

      this.scrollTimeout = setTimeout(() => {
        const container = this.$refs.container;
        const containerHeight = container.clientHeight;
        const scrollTop = container.scrollTop;

        // Calcular qué video está en el centro
        const newIndex = Math.round(scrollTop / containerHeight);

        if (newIndex !== this.currentVideoIndex && newIndex >= 0 && newIndex < this.videos.length) {
          this.currentVideoIndex = newIndex;

          // Agregar al historial
          bookmarksService.addToHistory(this.videos[newIndex]);

          // Verificar si necesitamos cargar más chunks
          this.checkAndLoadNextChunk();
        }

        this.isScrolling = false;
      }, 150);
    },
    isFavorite(video) {
      this.updateTrigger;
      return bookmarksService.isFavorite(video.id || video.link);
    },
    toggleFavorite(video) {
      if (this.isFavorite(video)) {
        bookmarksService.removeFromFavorites(video.id || video.link);
      } else {
        bookmarksService.addToFavorites(video);
      }
      this.updateTrigger++;
    },
    async shareVideo(video) {
      const success = await copyShareUrl(video);
      if (success) {
        this.showShareToast = true;
        setTimeout(() => {
          this.showShareToast = false;
        }, 2000);
      }
    },
    openInPlatform(video) {
      window.open(video.link, '_blank', 'noopener,noreferrer');
    },
    handleBookmarkUpdate() {
      this.updateTrigger++;
    },
    handleEscKey(event) {
      if (event.key === 'Escape') {
        // Navegar de vuelta a /news
        this.$router.push('/news');
      }
    },
    toggleControls() {
      // Mostrar controles al hacer tap
      this.showControls = true;

      // Ocultar automáticamente después de 3 segundos
      if (this.controlsTimeout) {
        clearTimeout(this.controlsTimeout);
      }

      this.controlsTimeout = setTimeout(() => {
        this.showControls = false;
      }, 3000);
    },
    handleContainerTap(event) {
      // Solo activar en móvil (ancho < 768px)
      if (window.innerWidth < 768) {
        // Evitar que el tap en botones active los controles
        if (!event.target.closest('.action-btn') &&
            !event.target.closest('.back-btn') &&
            !event.target.closest('.close-btn') &&
            !event.target.closest('.drawer-handle') &&
            !event.target.closest('.drawer-menu') &&
            !event.target.closest('.drawer-action-btn')) {
          this.toggleControls();
        }
      }
    },
    toggleDrawer() {
      this.showDrawer = !this.showDrawer;
    },
    closeDrawer() {
      this.showDrawer = false;
    },
    closeShorts() {
      // Cerrar Shorts y volver a /news
      this.$router.push('/news');
    }
  },
  async mounted() {
    await this.loadVideos();

    window.addEventListener('bookmarks-updated', this.handleBookmarkUpdate);
    window.addEventListener('keydown', this.handleEscKey);
    window.addEventListener('message', this.handlePlayerMessage);

    // Scroll snap - asegurar que se muestre el primer video
    this.$nextTick(() => {
      if (this.$refs.container) {
        this.$refs.container.scrollTop = 0;
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('bookmarks-updated', this.handleBookmarkUpdate);
    window.removeEventListener('keydown', this.handleEscKey);
    window.removeEventListener('message', this.handlePlayerMessage);
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }
  }
};
</script>

<style scoped>
.shorts-page {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100dvh; /* Dynamic viewport height - se ajusta a controles del navegador */
  background: #000;
  overflow: hidden;
  z-index: 1000;
  touch-action: pan-y; /* Sin zoom (pinch/doble tap) solo aquí; el resto del sitio sí permite zoom */
}

/* Botón X fijo para cerrar (solo móvil) */
.close-btn {
  display: none; /* Oculto por defecto en desktop */
  position: fixed;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 200;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1) rotate(90deg);
  border-color: rgba(255, 255, 255, 0.3);
}

.close-btn:active {
  transform: scale(0.95);
}

.sound-btn {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 14px;
  cursor: pointer;
  z-index: 200;
}

/* Header minimalista */
.shorts-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 20px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.8), transparent);
  z-index: 100;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.shorts-title {
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin: 0;
}

/* Contenedor con scroll snap */
.shorts-container {
  width: 100%;
  height: 100dvh; /* Dynamic viewport height */
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

/* Ocultar scrollbar pero mantener funcionalidad */
.shorts-container::-webkit-scrollbar {
  display: none;
}

.shorts-container {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Item individual (pantalla completa) */
.short-item {
  position: relative;
  width: 100%;
  height: 100dvh; /* Dynamic viewport height */
  scroll-snap-align: start;
  scroll-snap-stop: always;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}

/* Video wrapper - Formato móvil 9:16 centrado */
.video-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(100%, 450px); /* Ancho máximo de móvil */
  height: min(100dvh, calc(min(100%, 450px) * 16 / 9)); /* Aspect ratio 9:16 con dynamic viewport */
  max-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  border-radius: 0;
  overflow: hidden;
}

.video-iframe {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-placeholder {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  border-radius: inherit;
}

.video-placeholder img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.7);
  border-radius: inherit;
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: rgba(255, 0, 0, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* Contenedor de acciones */
.video-info {
  position: absolute;
  bottom: 80px;
  right: 20px;
  z-index: 10;
  pointer-events: none;
}

/* Acciones laterales */
.video-actions {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  pointer-events: auto;
}

.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 8px;
  pointer-events: auto;
}

.action-btn:hover {
  transform: scale(1.1);
}

.action-btn--active {
  color: #fbbf24;
}

.action-btn--active svg {
  fill: #fbbf24;
}

.action-label {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

/* Indicadores de navegación */
.navigation-hints {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 5;
  opacity: 0.3;
  transition: opacity 0.3s ease;
}

.short-item:hover .navigation-hints {
  opacity: 0.6;
}

.nav-hint {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: bounce 2s infinite;
}

.nav-hint--up {
  animation-delay: 0s;
}

.nav-hint--down {
  animation-delay: 1s;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

/* Toast de compartido */
.share-toast {
  position: fixed;
  bottom: max(100px, calc(env(safe-area-inset-bottom) + 80px)); /* Safe area para toast */
  left: 50%;
  transform: translateX(-50%);
  background: rgba(52, 211, 153, 0.95);
  color: white;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

/* ========== DRAWER LATERAL (Solo móvil) ========== */

/* Tab/Handle para abrir drawer */
.drawer-handle {
  display: none; /* Oculto por defecto (desktop) */
  position: fixed;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 80px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: none;
  border-radius: 0 12px 12px 0;
  color: white;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 150;
  box-shadow: 4px 0 12px rgba(0, 0, 0, 0.3);
}

.drawer-handle:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.2);
  width: 40px;
}

.drawer-handle--open {
  left: 100px; /* Se mueve con el drawer */
}

/* Overlay oscuro cuando drawer está abierto */
.drawer-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 100;
  pointer-events: none;
}

.drawer-overlay--visible {
  opacity: 1;
  pointer-events: auto;
}

/* Drawer menu lateral */
.drawer-menu {
  display: none; /* Oculto por defecto (desktop) */
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 100px;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  transform: translateX(-100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 110;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.5);
}

.drawer-menu--open {
  transform: translateX(0);
}

/* Contenido del drawer */
.drawer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 24px;
  padding: 20px 0;
}

/* Botones dentro del drawer */
.drawer-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 12px;
  border-radius: 12px;
  width: 80px;
}

.drawer-action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.05);
}

.drawer-action-btn:active {
  transform: scale(0.95);
}

.drawer-action-btn--active {
  color: #fbbf24;
}

.drawer-action-btn--active svg {
  fill: #fbbf24;
}

.drawer-action-label {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  text-align: center;
}

/* Responsive */
@media (max-width: 768px) {
  /* Mostrar y ajustar botón X en móvil */
  .close-btn {
    display: flex; /* Mostrar en móvil */
    top: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
  }

  /* Mostrar drawer handle en móvil */
  .drawer-handle {
    display: flex;
    left: max(0px, env(safe-area-inset-left)); /* Safe area para notch */
  }

  /* Mostrar drawer overlay en móvil */
  .drawer-overlay {
    display: block;
  }

  /* Mostrar drawer menu en móvil */
  .drawer-menu {
    display: block;
    left: max(0px, env(safe-area-inset-left)); /* Safe area para notch */
  }

  .drawer-handle--open {
    left: max(100px, calc(100px + env(safe-area-inset-left)));
  }

  /* OCULTAR botones laterales antiguos en móvil */
  .video-info {
    display: none !important;
  }

  .shorts-header {
    height: 50px;
    padding: 0 12px;
    /* Auto-hide en móvil - aparece solo al tocar */
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }

  /* Mostrar header al tocar la pantalla */
  .shorts-page.show-controls .shorts-header {
    opacity: 1;
    pointer-events: auto;
  }

  /* Usar small viewport height en móvil para mejor compatibilidad */
  .shorts-container {
    height: 100svh; /* Small viewport - asegura que siempre quepa completo */
  }

  .short-item {
    height: 100svh;
  }

  .shorts-page {
    height: 100svh;
  }

  .back-btn {
    width: 36px;
    height: 36px;
  }

  .shorts-title {
    font-size: 18px;
  }

  .video-info {
    /* Safe area inset para evitar notch y home indicator en iPhone */
    bottom: max(60px, calc(env(safe-area-inset-bottom) + 20px));
    right: max(12px, env(safe-area-inset-right));
  }

  .video-actions {
    gap: 16px;
  }

  .action-btn svg {
    width: 24px;
    height: 24px;
  }

  .action-label {
    font-size: 10px;
  }

  .navigation-hints {
    display: none;
  }

  /* Optimizar video wrapper para móvil - usar todo el espacio */
  .video-wrapper {
    width: 100%;
    height: 100%;
    max-height: 100svh; /* Asegura que quepa completo en móvil */
    /* Ajustar para safe areas */
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }
}

/* Landscape mode - más compacto */
@media (orientation: landscape) and (max-height: 600px) {
  .video-info {
    bottom: max(40px, calc(env(safe-area-inset-bottom) + 10px));
    right: max(12px, env(safe-area-inset-right));
  }

  .video-actions {
    gap: 12px;
  }

  .share-toast {
    bottom: max(60px, calc(env(safe-area-inset-bottom) + 40px));
  }
}
</style>
